/**
 * SessionCompletionSummary Component
 * Displays a calm, centered completion view when a tutoring session ends
 * Shows pebbles earned, first-try streak (if applicable), improvement tip, and navigation CTAs
 *
 * Design principles (BR-015):
 * - Calm and encouraging, never celebratory or competitive
 * - NO animations, confetti, or celebration graphics
 * - Static, quiet visual presentation with warm ThinkOtter colors
 */

import { useNavigate } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { PebblesDisplay } from '@/components/PebblesDisplay';
import { StreakDisplay } from '@/components/StreakDisplay';
// US-019 T3: reuse existing StarRating primitive (AC-1, AC-2, AC-3)
import { StarRating } from '@/components/StarRating';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getStruggledTypeLabels } from '@/lib/questionTypeLabels';
import type { SessionSummary } from '@/types/session';

export interface SessionCompletionSummaryProps {
  /** Total pebbles earned in this session */
  totalPebblesEarned: number;
  /** Number of consecutive first-try correct answers */
  firstTryStreak: number;
  /** Total questions answered in the session */
  totalQuestions: number;
  /** Session-specific encouraging improvement tip */
  improvementTip: string;
  /** Optional serverless session summary with structured performance data */
  sessionSummary?: SessionSummary | null;
  /**
   * Server-derived star rating (3 | 4 | 5) for completed sessions; null for
   * cancelled/non-rated sessions and legacy responses. Frontend must NEVER
   * recompute this value — it is passed through from useSessionMessages which
   * reads derive_star_rating() output (DD-1, TS-014, TO-LL-024, TO-LL-044).
   * Stars are shown only when starRating > 0; null renders nothing (AC-3).
   * CCM-03: no financial/coin data — only a numeric rating is rendered.
   *
   * Implements US-019 T3.
   */
  starRating?: number | null;
  /**
   * first-run-guide (T11): true when the person looking at this is the PARENT, driving her
   * child's session, rather than the student whose session it is.
   *
   * ⚠️ Why this exists. Both CTAs below send you to /sessions, which is the STUDENT's session
   * list. That was always correct while only a student could reach this screen. Now a parent
   * can drive a session to completion, and for her that page is her own empty session list
   * with a "Start a new session" button on it — and that button calls the student start, so it
   * would create a session with HER as the student. That is exactly the parent self-trial that
   * was deliberately removed, reachable again through a different door, one tap from the end of
   * her child's first session.
   *
   * Found by Wayne testing in UAT, 2026-07-27.
   */
  isDrivingForChild?: boolean;
  /** The child's display name, for the parent-facing copy. */
  childName?: string;
  /** Where to send the parent instead of the student session list. */
  onBackToParentDashboard?: () => void;
}

/**
 * SessionCompletionSummary - Calm completion view for finished sessions
 *
 * Features:
 * - Displays pebbles earned using PebblesDisplay (lg size) - quiet, no animations
 * - Shows first-try streak using StreakDisplay (md size) - only when streak > 0
 * - Shows one encouraging improvement tip (under 200 chars)
 * - Provides two navigation CTAs: "View My Sessions" and "Start Another Session"
 * - Uses ThinkOtter warm color palette (otter-paper bg, otter-earth text, #E7D8C8 border)
 * - NO animations, confetti, or celebration graphics per BR-015
 *
 * Accessibility:
 * - ARIA region label for screen readers
 * - Semantic heading hierarchy (h2 for main heading)
 * - 44px minimum touch targets for mobile (h-11 buttons)
 * - WCAG AA contrast ratios
 *
 * @example
 * ```tsx
 * <SessionCompletionSummary
 *   totalPebblesEarned={24}
 *   firstTryStreak={2}
 *   totalQuestions={5}
 *   improvementTip="You started strong with 2 first-try answers!"
 * />
 * ```
 */
export function SessionCompletionSummary({
  totalPebblesEarned,
  firstTryStreak,
  improvementTip,
  sessionSummary,
  starRating,
  isDrivingForChild = false,
  childName,
  onBackToParentDashboard,
}: SessionCompletionSummaryProps) {
  const navigate = useNavigate();

  // Use fallback tip if empty
  const displayTip =
    improvementTip.trim() || 'Every practice session builds stronger reading skills. Keep it up!';

  // Extract serverless summary data if available
  const questionsAttempted = sessionSummary?.questionsAttempted ?? 0;
  const firstAttemptCorrect = sessionSummary?.firstAttemptCorrect ?? 0;
  const struggledTypeLabels = getStruggledTypeLabels(sessionSummary?.questionTypesStruggled);
  const hasStruggledTypes = struggledTypeLabels.length > 0;

  return (
    <div role="region" aria-label="Session completion summary" className="w-full py-6 px-2">
      <Card className="max-w-md mx-auto overflow-hidden rounded-3xl border-otter-sand bg-otter-paper shadow-md">
        {/* Warm header band — calm (no animation), warm gradient with an otter seal */}
        <div
          className="flex flex-col items-center gap-3 px-6 pt-7 pb-5 text-center"
          style={{
            background:
              'radial-gradient(120% 100% at 50% 0%, rgba(255,255,255,0.7), transparent 45%), linear-gradient(150deg, #fff7ed, #ffedd5 60%, #fed7aa)',
          }}
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm">
            <CheckCircle className="h-8 w-8 text-green-600" aria-hidden="true" />
          </div>
          {/* first-run-guide (T11): name the child when a parent is the one looking at this,
              so the screen stays consistent with the rest of the session, which has been
              telling her all along that this is her child's work and not her own. */}
          <h2 className="font-display text-2xl font-semibold text-otter-earth-800">
            {isDrivingForChild && childName ? `${childName}'s practice is complete` : 'Session Complete'}
          </h2>
        </div>

        <CardContent className="p-6 space-y-4">

          {/* Metrics section */}
          <div className="space-y-3">
            {/* US-019 T3: Star rating — shown prominently above pebbles for completed sessions.
                AC-1: 5/4/3 stars displayed per server-derived threshold.
                AC-2: Stars only — no fraction text (StarRating renders icons only).
                AC-3: null starRating (cancelled/non-rated) renders nothing (StarRating returns null for null/<=0).
                BR-015: static display, no animation.
                CCM-03: no financial/coin data.
                DD-1: value is server-derived (TS-014) — never recomputed here.
                Enforces TO-LL-024 (Decimal->int cast already done server-side; FE treats as number|null).
                Enforces TO-LL-044 (key name 'starRating' matches backend camelCase exactly). */}
            {starRating != null && starRating > 0 && (
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm text-otter-earth-700">Your Rating</span>
                <StarRating rating={starRating} size="md" />
              </div>
            )}

            {/* Pebbles earned */}
            <div className="flex flex-col items-center gap-2">
              <span className="text-sm text-otter-earth-700">Pebbles Earned</span>
              <PebblesDisplay totalPebbles={totalPebblesEarned} size="lg" />
            </div>

            {/* Session results (serverless summary data if available) */}
            {sessionSummary && questionsAttempted > 0 && (
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm text-otter-earth-700">Session Results</span>
                <p className="text-base text-otter-earth-800 font-medium">
                  {questionsAttempted} question{questionsAttempted !== 1 ? 's' : ''} attempted,{' '}
                  {firstAttemptCorrect} correct on first try
                </p>
              </div>
            )}

            {/* First-try streak (only shown if streak > 0 per BR-017) */}
            {firstTryStreak > 0 && (
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm text-otter-earth-700">First-Try Streak</span>
                <StreakDisplay streak={firstTryStreak} size="md" />
              </div>
            )}

            {/* Areas for improvement (only shown when struggled types exist) */}
            {hasStruggledTypes && (
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm text-otter-earth-700">Areas for Improvement</span>
                <div className="flex flex-wrap justify-center gap-2">
                  {struggledTypeLabels.map((label) => (
                    <Badge
                      key={label}
                      variant="outline"
                      className="border-otter-orange-400 text-otter-orange-700"
                    >
                      {label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Improvement tip */}
          <div className="bg-otter-sand/60 rounded-2xl p-3.5">
            <p className="text-sm text-otter-earth-700 text-center leading-relaxed">{displayTip}</p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col gap-2 pt-2">
            {isDrivingForChild ? (
              /*
                Parent view. ONE action, back to her own dashboard, where the guide is waiting
                with the report. Deliberately no "start another": the whole point of the guide's
                start button is that it waits for the child to be ready, and offering another
                session at the moment the last one ended would spend a session the family may
                not have meant to spend.
              */
              <Button
                variant="warm"
                className="h-11 w-full"
                onClick={onBackToParentDashboard}
              >
                Back to my dashboard
              </Button>
            ) : (
              <>
                <Button variant="warm" className="h-11 w-full" onClick={() => navigate('/sessions')}>
                  View My Sessions
                </Button>
                <Button variant="outline" className="h-11 w-full" onClick={() => navigate('/sessions')}>
                  Start Another Session
                </Button>
              </>
            )}
            {/* US-009 / FR-2.1, FR-2.2: no feedback control here on purpose. Sharing
                feedback is a grown-up action, so it lives on the parent dashboard
                (see ParentFeedbackSheet, US-008). The audience of this card is a
                P4 to P6 student, and a parent driving her child's session reaches
                the sheet from her own dashboard, not from here.
                Enforces TO-LL-021 (no duplicate affordance for an action a parent
                already has elsewhere) and BR-009.1 (no student-facing feedback
                surface in v1). */}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
